import {$s} from '@bitstillery/common/app'
import {deref} from '@bitstillery/common/utils'
import m from '@bitstillery/mithril'

export enum AccountSlug {
    A2BC = 'a2bc',
    ETR = 'etr',
    MSI = 'msi',
    MSP = 'msp',
    DG = 'dg',
}

const account_slugs: readonly string[] = Object.values(AccountSlug)

/** API rows type an account slug as a plain string; this tells whether it names an account. */
function is_account_slug(slug: string): slug is AccountSlug {
    return account_slugs.includes(slug)
}

/**
 * An account as callers hold one: v1 JSON and API rows carry the slug as a plain string, and a legacy
 * model carries both fields as props.
 */
interface Account {
    slug: string | (() => string)
    name?: string | (() => string)
}

export function account_icon(account: Account) {
    return account_icon_by_slug(account.slug, account.name)
}

export function account_icon_by_slug(account_slug: string | (() => string), account_name?: string | (() => string)): m.Vnode {
    account_slug = deref(account_slug)
    const known_slug = is_account_slug(account_slug) ? account_slug : undefined
    let icon_src: string | undefined

    if (process.env.MSI_PACKAGE === 'portal') {
        // Don't show as dead accounts for customers.
        if (known_slug === AccountSlug.A2BC || known_slug === AccountSlug.ETR) {
            icon_src = '/common/img/etr-favicon.ico'
        } else if (known_slug === AccountSlug.MSI || known_slug === AccountSlug.MSP) {
            icon_src = '/common/img/msp-favicon.ico'
        }
    } else if (process.env.MSI_PACKAGE === 'discover') {
        if (known_slug === AccountSlug.A2BC) {
            icon_src = '/common/img/a2bc-favicon.ico'
        } else if (known_slug === AccountSlug.ETR) {
            icon_src = '/common/img/etr-favicon.ico'
        } else if (known_slug === AccountSlug.MSI) {
            icon_src = '/common/img/msi-favicon.ico'
        } else if (known_slug === AccountSlug.MSP) {
            icon_src = '/common/img/msp-favicon.ico'
        }
    }

    const name: string | undefined = deref(account_name)
    return <img className='account-slug-icon' src={icon_src} title={name} alt={`${name ?? account_slug}-favicon.ico`} />
}

export function account_secondary_color_rgb(slug: string): {
    red: number
    green: number
    blue: number
} {
    if (!is_account_slug(slug)) {
        throw new Error('Unknown account slug')
    }
    if (slug === AccountSlug.A2BC) {
        return {
            blue: 160,
            green: 203,
            red: 255,
        }
    } else if (slug === AccountSlug.ETR) {
        return {
            blue: 236,
            green: 236,
            red: 248,
        }
    } else if (slug === AccountSlug.MSI) {
        return {
            blue: 246,
            green: 234,
            red: 202,
        }
    } else if (slug === AccountSlug.MSP) {
        return {
            blue: 236,
            green: 236,
            red: 248,
        }
    }
    throw new Error('Unknown account slug')
}

export function account_primary_color_rgb_list(slug: string): {
    red: number
    green: number
    blue: number
} {
    if (!is_account_slug(slug)) {
        throw new Error('Unknown account slug')
    }
    if (slug === AccountSlug.A2BC) {
        return {
            blue: 39,
            green: 99,
            red: 250,
        }
    } else if (slug === AccountSlug.ETR) {
        return {
            blue: 39,
            green: 99,
            red: 250,
        }
    } else if (slug === AccountSlug.MSI) {
        return {
            blue: 167,
            green: 107,
            red: 42,
        }
    } else if (slug === AccountSlug.MSP) {
        return {
            blue: 167,
            green: 107,
            red: 42,
        }
    }
    throw new Error('Unknown account slug')
}

export function account_pdf_logo_src(slug: string): string {
    if (!is_account_slug(slug)) {
        throw new Error('Unknown account slug')
    }
    if (slug === AccountSlug.A2BC) {
        return '/common/img/pdf-logo.a2bc.jpg'
    } else if (slug === AccountSlug.ETR) {
        return '/common/img/pdf-logo.etr.jpg'
    } else if (slug === AccountSlug.MSI) {
        return '/common/img/pdf-logo.msi.jpg'
    } else if (slug === AccountSlug.MSP) {
        return '/common/img/pdf-logo.msp.jpg'
    }

    throw new Error('Unknown account slug')
}

// Per-account branding = hue + chroma ONLY. These are theme-invariant, so setting them inline is safe.
// NEVER set --l-* here: lightness is the light/dark theme axis (theme/tokens.css), and an inline
// --l-* would beat the [data-theme="dark"] rules and pin dark mode to light-mode lightness (dark text
// on dark surfaces). Per-account lightness tweaks belong in the theme's light map, not here.
//
// Every account declares EVERY property, deliberately — omitting one does not fall back to a neutral
// default, it falls back to whatever is already on <html>:
//  - Discover only ever builds with theme/msp (there is no per-brand Discover bundle), so the compiled
//    :root is MSP's. ETR used to omit --h-chrome and inherit MSP's 212deg teal for every chrome band.
//  - switching accounts does not reload the page, so a property the previous account set inline stays
//    set. Coming from A2BC or MSI, ETR kept their desaturating --c-info.
// Keep in sync with theme/etr|msp/index.css and pdfium/public/styles.css.
const ACCOUNT_BRANDING: Partial<Record<string, Record<string, string>>> = {
    [AccountSlug.A2BC]: {
        '--c-brand': '0.05',
        '--c-info': '0.05',
        '--h-brand': '32deg',
        '--h-chrome': '248deg',
        '--h-info': '248deg',
        '--h-success': '148deg',
        '--h-surface': '43deg',
    },
    [AccountSlug.ETR]: {
        '--c-brand': '0.14',
        '--c-info': '0.1',
        '--h-brand': '32deg',
        // ETR does not split its chrome bands off its info hue the way MSP does; both are the same blue.
        '--h-chrome': '248deg',
        '--h-info': '248deg',
        '--h-success': '148deg',
        '--h-surface': '43deg',
    },
    [AccountSlug.MSI]: {
        '--c-brand': '0.06',
        '--c-info': '0.05',
        '--h-brand': '14deg',
        '--h-chrome': '212deg',
        '--h-info': '235deg',
        '--h-success': '148deg',
        '--h-surface': '63deg',
    },
    [AccountSlug.MSP]: {
        '--c-brand': '0.18',
        '--c-info': '0.1',
        '--h-brand': '14deg',
        // Chrome bands (header, footer, cart + drawer titles) keep the brand's dark teal while the info
        // tints sit bluer — mirrors theme/msp/index.css, which these inline styles outrank.
        '--h-chrome': '212deg',
        '--h-info': '235deg',
        '--h-success': '148deg',
        '--h-surface': '63deg',
    },
}

export function set_account_styles(slug: string | (() => string)) {
    const root = document.documentElement
    slug = deref(slug)

    const branding = ACCOUNT_BRANDING[slug]
    if (branding) {
        for (const [property, value] of Object.entries(branding)) {
            root.style.setProperty(property, value)
        }
        document.title = `Discover [${slug.toUpperCase()}]`
    }

    const icon_link = document.querySelector("link[rel~='icon']")
    let link = icon_link instanceof HTMLLinkElement ? icon_link : null
    if (!link) {
        link = document.createElement('link')
        link.rel = 'icon'
        document.head.append(link)
    }
    link.href = `/theme/img/discover/${slug}-favicon.ico`
}

export function current_account_slug() {
    // Module-level data literals call this before store.load(), when $s has no tab tier yet; and the
    // portal has no account in its tab tier, so it falls back to its theme.
    return $s.tab?.account?.slug ?? process.env.MSI_THEME ?? 'a2bc'
}
